import { flowStepTitle } from '@/lib/flow-step-presentation'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { testIds } from '@/lib/test-ids'

type Step = {
  id?: string
  inputs?: Record<string, unknown>
}

const FIELD_ADD =
  'border-green-700/40 bg-green-600/[0.16] dark:border-green-400/75 dark:bg-green-500/[0.24]'
const FIELD_REMOVE =
  'rounded-md border border-red-700/35 bg-red-600/[0.12] px-2 py-1 text-xs text-red-950/80 line-through decoration-red-700/45 dark:border-red-400/70 dark:bg-red-500/20 dark:text-red-100/80 dark:decoration-red-400/60'

function stepSentence(step: Step, index: number) {
  const title = flowStepTitle(step.id) || step.id || `Step ${index + 1}`
  const inputs = step.inputs && typeof step.inputs === 'object' ? step.inputs : {}
  const bits = Object.entries(inputs)
    .slice(0, 3)
    .map(([key, value]) => {
      const text = typeof value === 'string' ? value : JSON.stringify(value)
      const short = text.length > 80 ? `${text.slice(0, 77)}...` : text
      return `${key}: ${short}`
    })
  return { title, detail: bits.join(' · ') }
}

function literalFields(inputs: Record<string, unknown> | undefined): Array<[string, string]> {
  if (!inputs) return []
  return Object.entries(inputs).filter((entry): entry is [string, string] => typeof entry[1] === 'string')
}

export function FlowDefinitionPipeline({
  steps,
  triggerSummary,
  lastNext,
  selectedIndex,
  onSelect,
  pendingFields,
  onCommitField,
}: {
  steps: Step[]
  triggerSummary: string
  lastNext: string
  selectedIndex?: number | null
  onSelect?: (index: number) => void
  /** Parked edits, keyed `index:field`. Live flows hold these until Approve. */
  pendingFields?: Record<string, string>
  onCommitField?: (index: number, field: string, value: string) => void
}) {
  return (
    <div className="flex w-full flex-col gap-3" data-testid={testIds.flowDocumentRenderer.definitionPipeline}>
      <div className="rounded-lg border border-border bg-card p-4">
        <p className="text-sm font-medium">Schedule</p>
        <p className="mt-1 text-sm text-muted-foreground">{triggerSummary}</p>
        <p className="mt-1 text-xs text-muted-foreground">{lastNext}</p>
      </div>
      {steps.length === 0 ? (
        <p className="text-sm text-muted-foreground">This flow has no steps yet.</p>
      ) : (
        steps.map((step, index) => {
          const sentence = stepSentence(step, index)
          const selected = selectedIndex === index
          const fields = literalFields(step.inputs)
          const changed = fields.some(([field, saved]) => {
            const pending = pendingFields?.[`${index}:${field}`]
            return pending != null && pending !== saved
          })
          return (
            <div
              key={`${step.id || 'step'}-${index}`}
              className={cn(
                'rounded-lg border bg-card p-4',
                changed
                  ? 'border-green-700/40 bg-green-600/[0.16] dark:border-green-400/75 dark:bg-green-500/[0.24]'
                  : selected
                    ? 'border-primary bg-primary/5'
                    : 'border-border',
              )}
            >
              <button
                type="button"
                className="w-full text-left"
                aria-pressed={selected}
                data-testid={testIds.flowDocumentRenderer.definitionStep(index)}
                onClick={() => onSelect?.(index)}
              >
                <p className="text-sm font-medium">
                  {index + 1}. {sentence.title}
                </p>
                {!selected && sentence.detail ? (
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{sentence.detail}</p>
                ) : null}
              </button>
              {selected && fields.length > 0 ? (
                <div className="mt-3 space-y-3">
                  {fields.map(([field, saved]) => {
                    const pending = pendingFields?.[`${index}:${field}`]
                    const shown = pending ?? saved
                    const dirty = pending != null && pending !== saved
                    return (
                      <label key={field} className="block space-y-1">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          {field}
                        </span>
                        {dirty ? (
                          <p className={FIELD_REMOVE}>{saved || 'empty'}</p>
                        ) : null}
                        <Input
                          defaultValue={shown}
                          key={`${index}:${field}:${shown}`}
                          className={cn('h-8 text-sm', dirty && FIELD_ADD)}
                          data-testid={testIds.flowDocumentRenderer.definitionField(index, field)}
                          onBlur={(event) => onCommitField?.(index, field, event.target.value)}
                        />
                      </label>
                    )
                  })}
                </div>
              ) : null}
            </div>
          )
        })
      )}
    </div>
  )
}
